import type { CartLine, InventoryRecord } from "@/types/domain";

type AvailabilityLine = Pick<
  CartLine,
  "item_no" | "qty" | "is_third_party" | "source_type"
>;

type AvailabilityRecord = Pick<
  InventoryRecord,
  "itemNo" | "locationCode" | "effectiveAvailable" | "quantityAvailable"
>;

export function normalizeInventoryIdentifier(value: string): string {
  return value.trim().toUpperCase();
}

export function availableAtLocation(
  records: AvailabilityRecord[],
  itemNo: string,
  locationCode: string,
): number {
  const normalizedItemNo = normalizeInventoryIdentifier(itemNo);
  const normalizedLocationCode = normalizeInventoryIdentifier(locationCode);
  return records.reduce((total, record) => {
    if (
      normalizeInventoryIdentifier(record.itemNo) !== normalizedItemNo ||
      normalizeInventoryIdentifier(record.locationCode) !==
        normalizedLocationCode
    )
      return total;
    return (
      total +
      Math.max(
        0,
        Number(record.effectiveAvailable ?? record.quantityAvailable ?? 0),
      )
    );
  }, 0);
}

export function lineRequiresBackOrder(
  line: AvailabilityLine,
  available: number,
): boolean {
  const isThirdParty =
    line.is_third_party || line.source_type === "THIRD_PARTY";
  return !isThirdParty && line.qty > Math.max(0, available);
}

export function backOrderStatusByItem(
  lines: AvailabilityLine[],
  records: AvailabilityRecord[],
  locationCode: string,
): Record<string, boolean> {
  return Object.fromEntries(
    lines.map((line) => [
      line.item_no,
      lineRequiresBackOrder(
        line,
        availableAtLocation(records, line.item_no, locationCode),
      ),
    ]),
  );
}
